import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { PlayerProfileScopeSwitcher } from "../components/PlayerFamilyProfileSwitcher";
import { makeLatestOnly } from "../lib/latestOnly";
import { usePlayerProfileScope } from "../lib/usePlayerProfileScope";
import PlayerPhotoChip from "../components/PlayerPhotoChip";
import PlayerTournamentBottomNav from "../components/PlayerTournamentBottomNav";
import TournamentSponsorSlots from "../components/TournamentSponsorSlots";
import {
  api,
  ApiError,
  type DashboardMatchParticipantBrief,
  type PlayerGroupContactMember,
  type PlayerStandingsPayload,
  type RetoSender,
  type StandingsRow,
} from "../lib/api";
import { participantStatusLabel } from "../lib/playerLabels";
import { buildPlayerCategorySections, type PlayerCategorySection, type PlayerGroupSection } from "../lib/playerStage";
import {
  firstName,
  guardianPhoneLabel,
  isTeamSide,
  retoRivalContacts,
  retoWhatsappMessage,
  whatsappHref,
} from "../lib/retoContacto";

/** Celular(es) de un integrante de SU grupo de round robin con WhatsApp y
 * Llamar (Eduardo 2026-10-07: adelantar jornadas). Mismas reglas que el
 * reto: un menor sin celular propio trae el de su papá, mamá o tutor (con
 * la leyenda) y una pareja, los dos. El servidor solo manda los de su
 * grupo. */
function GroupMemberContact({
  member,
  sender,
  sport,
}: {
  member: PlayerGroupContactMember;
  sender: RetoSender | null;
  sport?: string | null;
}) {
  const side: DashboardMatchParticipantBrief = {
    participant_id: member.participant_id,
    player_profile_id: member.player_profile_id,
    team_id: member.team_id,
    participant_kind: member.participant_kind,
    display_name: member.display_name,
    phone: null,
    contacts: member.contacts,
  };
  const contacts = retoRivalContacts(side);
  if (contacts.length === 0) return null;
  const team = isTeamSide(side);
  return (
    <div className="player-group-contact" data-no-toast aria-label={`Contacto de ${member.display_name ?? "jugador"}`}>
      {contacts.map((contact) => {
        const name = team ? firstName(contact.name) : (contact.name ?? member.display_name);
        const message = retoWhatsappMessage(sender, sport);
        const suffix = team ? ` ${firstName(contact.name)}` : "";
        return (
          <div key={contact.digits} className="player-group-contact__row">
            <span className="player-group-contact__phone">
              {contact.guardian ? `${guardianPhoneLabel(name)}: ` : team ? `${name}: ` : ""}
              {contact.phone}
            </span>
            <a
              className="button button--sm player-group-contact__whatsapp"
              href={whatsappHref(contact.digits, message)}
              target="_blank"
              rel="noreferrer"
            >
              💬 WhatsApp{suffix}
            </a>
            <a className="button button--sm button--ghost" href={`tel:${contact.digits}`}>
              📞 Llamar{suffix}
            </a>
          </div>
        );
      })}
    </div>
  );
}

/** Jugadores del torneo por CATEGORÍA y, dentro, por grupo activo (Eduardo
 * 2026-09-24): se abre la categoría del jugador y su grupo; sin grupos, la
 * categoría lista a todos sus inscritos. Es lo único que el jugador ve
 * hasta que el admin termina el wizard. */

const EMPTY_CONTACTS: ReadonlyMap<number, PlayerGroupContactMember> = new Map();

export default function PlayerTournamentPlayersPage() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token } = useAuth();
  const [data, setData] = useState<PlayerStandingsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openKeys, setOpenKeys] = useState<Set<string> | null>(null);
  // Celulares de SU grupo de round robin (participant_id → integrante); el
  // servidor solo los manda sin calendario de canchas y con el torneo abierto.
  // Quien escribe el WhatsApp: nombre y apellido del perfil propio elegido
  // (del servidor, con los celulares), nunca la etiqueta de la pareja. Los
  // dos llevan la clave del perfil y torneo que los PIDIÓ (bloqueo de Torni,
  // PR #581): dos perfiles de la cuenta pueden compartir la pareja.
  const [contactsState, setContactsState] = useState<{
    key: string;
    members: Map<number, PlayerGroupContactMember>;
    sender: RetoSender | null;
  } | null>(null);
  // Cuenta familiar (Eduardo 2026-09-29): «Tú», tu grupo y tu partido son
  // los del perfil elegido para este torneo.
  const scope = usePlayerProfileScope({ tournamentId });
  const scopeProfileId = scope.profileId;
  const latestLoad = useMemo(() => makeLatestOnly(), []);

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId) || !scope.ready) return;
    const attempt = latestLoad.begin();
    setLoading(true);
    setError(null);
    // Otro perfil (cuenta familiar): nunca se quedan los celulares del grupo anterior.
    setContactsState(null);
    const requestedKey = `${tournamentId}:${scopeProfileId ?? "self"}`;
    try {
      const [response, contactsResponse] = await Promise.all([
        api.standings.forPlayer(token, tournamentId, scopeProfileId),
        // Opcional: si falla, la lista de jugadores sale igual, sin celulares.
        api.standings.groupContactsForPlayer(token, tournamentId, scopeProfileId).catch(() => null),
      ]);
      if (!latestLoad.isCurrent(attempt)) return;
      setData(response.data);
      setContactsState({
        key: requestedKey,
        members: new Map((contactsResponse?.data?.members ?? []).map((member) => [member.participant_id, member])),
        sender: contactsResponse?.data?.sender ?? null,
      });
      // Otro perfil: se vuelven a abrir SU categoría y SU grupo.
      setOpenKeys(null);
    } catch (err) {
      if (!latestLoad.isCurrent(attempt)) return;
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar los jugadores del torneo.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      if (latestLoad.isCurrent(attempt)) setLoading(false);
    }
  }, [latestLoad, scope.ready, scopeProfileId, token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const myId = data?.my_participant_id ?? null;
  // Solo los celulares y el remitente pedidos para ESTE perfil y torneo.
  const ownContacts = contactsState?.key === `${tournamentId}:${scopeProfileId ?? "self"}` ? contactsState : null;
  const groupContacts = ownContacts?.members ?? EMPTY_CONTACTS;
  const sender = ownContacts?.sender ?? null;

  const sections = useMemo<PlayerCategorySection[]>(
    () => (data ? buildPlayerCategorySections(data.ranking ?? [], data.groups ?? [], myId) : []),
    [data, myId],
  );

  const totalPlayers = useMemo(
    () => sections.reduce((sum, section) => sum + section.total, 0),
    [sections],
  );
  const hasGroups = sections.some((section) => section.groups.length > 0);

  // La categoría del jugador abre sola (y dentro, su grupo); sin categoría
  // propia, la primera.
  const effectiveOpen = useMemo(() => {
    if (openKeys) return openKeys;
    const mine = sections.find((section) => section.isMine) ?? sections[0];
    const keys = new Set<string>(mine ? [`cat:${mine.key}`] : []);
    if (mine) {
      const myGroup = mine.groups.find((group) => group.isMine) ?? (mine.rows.some((row) => row.participant_id === myId) ? null : mine.groups[0]);
      if (myGroup) keys.add(`grp:${myGroup.key}`);
      else if (mine.groups.length > 0 && mine.rows.length > 0) keys.add(`rest:${mine.key}`);
    }
    return keys;
  }, [openKeys, sections, myId]);

  function toggle(key: string, open: boolean) {
    setOpenKeys((prev) => {
      const next = new Set(prev ?? effectiveOpen);
      if (open) next.add(key);
      else next.delete(key);
      return next;
    });
  }


  function renderRows(rows: StandingsRow[], numbered: boolean, withContacts = false) {
    return (
      <div className="responsive-table-wrap">
        <table className="data-table player-tournament-players-table">
          <thead>
            <tr>
              <th className="standings-col--num">{numbered ? "Rank" : "#"}</th>
              <th>Jugador</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const isMe = row.participant_id === myId;
              const contactMember = withContacts && !isMe ? groupContacts.get(row.participant_id) : undefined;
              return (
                <tr key={row.participant_id} className={isMe ? "standings-row--me" : undefined}>
                  <td className="standings-col--num">{numbered ? row.group_position ?? row.position : index + 1}</td>
                  <td>
                    <span className="player-name-with-photo">
                      <PlayerPhotoChip
                        name={row.display_name}
                        photoUrl={data?.photos?.[String(row.participant_id)] ?? null}
                        subtitle={row.group_name ?? null}
                      />
                      {row.team_members && row.team_members.length > 0 ? (
                        // Pareja/equipo (Eduardo 2026-09-17): cada integrante
                        // enlaza a su propio perfil.
                        <strong className="player-name-pair">
                          {row.team_members.map((member, memberIndex) => (
                            <span key={member.player_profile_id}>
                              {memberIndex > 0 ? " / " : ""}
                              <Link className="player-name-action" to={`/player/profiles/${member.player_profile_id}`}>
                                {member.display_name}
                              </Link>
                            </span>
                          ))}
                        </strong>
                      ) : row.player_profile_id ? (
                        <Link className="player-name-action" to={`/player/profiles/${row.player_profile_id}`}>
                          <strong>{row.display_name}</strong>
                        </Link>
                      ) : (
                        <strong>{row.display_name}</strong>
                      )}
                    </span>
                    {isMe && <span className="status-pill status-pill--active">Tú</span>}
                    {contactMember ? <GroupMemberContact member={contactMember} sender={sender} sport={data?.sport} /> : null}
                  </td>
                  <td>{participantStatusLabel(row.current_status)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  function renderGroup(group: PlayerGroupSection) {
    const key = `grp:${group.key}`;
    return (
      <details
        key={key}
        className={`card group-accordion player-games-group${group.isMine ? " is-player-group" : ""}`}
        open={effectiveOpen.has(key)}
        onToggle={(event) => toggle(key, event.currentTarget.open)}
      >
        <summary className="group-accordion__summary">
          <span className="group-accordion__title-wrap">
            <span className="group-accordion__title">
              {group.name}
              {group.isMine && <span className="status-pill status-pill--active">Tu grupo</span>}
            </span>
            <span className="group-accordion__meta">
              {group.rows.length} jugador{group.rows.length === 1 ? "" : "es"}
            </span>
          </span>
          <span className="group-accordion__chevron" aria-hidden="true">▾</span>
        </summary>
        <div className="group-accordion__body">
          {group.isMine && groupContacts.size > 0 ? (
            <p className="muted player-group-contact__hint">
              Coordina con tu grupo para adelantar jornadas: WhatsApp o llamada.
            </p>
          ) : null}
          {renderRows(group.rows, true, group.isMine)}
        </div>
      </details>
    );
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  return (
    <>
      {/* Secciones del torneo (Eduardo 2026-10-05): en computadora, pestañas
          aquí arriba; en celular, la barra flotante de abajo. */}
      <PlayerTournamentBottomNav tournamentId={tournamentId} />
      <section className="card">
        <div className="card__header-row">
          <div>
            <h1 className="card__title">Jugadores</h1>
            <p className="card__subtitle">
              {totalPlayers > 0
                ? `${totalPlayers} jugador${totalPlayers === 1 ? "" : "es"} inscrito${totalPlayers === 1 ? "" : "s"} en ${sections.length} categoría${sections.length === 1 ? "" : "s"}.${hasGroups ? " Tu categoría y tu grupo abren primero." : " Tu categoría abre primero."}`
                : "Consulta los jugadores inscritos en este torneo."}
            </p>
          </div>
        </div>
        <PlayerProfileScopeSwitcher scope={scope} tournament />
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
      </section>

      {loading ? (
        <section className="card">
          <p className="muted">Cargando jugadores…</p>
        </section>
      ) : sections.length === 0 ? (
        <section className="card">
          <p className="muted">Todavía no hay jugadores visibles para este torneo.</p>
        </section>
      ) : (
        <section className="player-games-list" aria-label="Jugadores por categoría">
          {sections.map((section) => {
            const key = `cat:${section.key}`;
            const restKey = `rest:${section.key}`;
            return (
              <details
                key={key}
                className={`card group-accordion player-games-group player-category-accordion${section.isMine ? " is-player-group" : ""}`}
                open={effectiveOpen.has(key)}
                onToggle={(event) => toggle(key, event.currentTarget.open)}
              >
                <summary className="group-accordion__summary">
                  <span className="group-accordion__title-wrap">
                    <span className="group-accordion__title">
                      {section.name}
                      {section.isMine && <span className="status-pill status-pill--active">Tu categoría</span>}
                    </span>
                    <span className="group-accordion__meta">
                      {section.total} jugador{section.total === 1 ? "" : "es"}
                      {section.groups.length > 0 ? ` · ${section.groups.length} grupo${section.groups.length === 1 ? "" : "s"}` : ""}
                    </span>
                  </span>
                  <span className="group-accordion__chevron" aria-hidden="true">▾</span>
                </summary>
                <div className="group-accordion__body">
                  {section.groups.length === 0 ? (
                    renderRows(section.rows, false)
                  ) : (
                    <div className="player-category-groups">
                      {section.groups.map((group) => renderGroup(group))}
                      {section.rows.length > 0 ? (
                        <details
                          className={`card group-accordion player-games-group${!section.groups.some((g) => g.isMine) && section.rows.some((row) => row.participant_id === myId) ? " is-player-group" : ""}`}
                          open={effectiveOpen.has(restKey)}
                          onToggle={(event) => toggle(restKey, event.currentTarget.open)}
                        >
                          <summary className="group-accordion__summary">
                            <span className="group-accordion__title-wrap">
                              <span className="group-accordion__title">Sin grupo</span>
                              <span className="group-accordion__meta">
                                {section.rows.length} jugador{section.rows.length === 1 ? "" : "es"}
                              </span>
                            </span>
                            <span className="group-accordion__chevron" aria-hidden="true">▾</span>
                          </summary>
                          <div className="group-accordion__body">{renderRows(section.rows, false)}</div>
                        </details>
                      ) : null}
                    </div>
                  )}
                </div>
              </details>
            );
          })}
        </section>
      )}

      <TournamentSponsorSlots compact />
    </>
  );
}
